import React, { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSettings } from "../../context/SettingsContext";
import aflPlayerData from "../../assets/afl_player_data_2026.json";
import { getPlayerRoundsByDataset } from "../Dashboard/playerRoundVotes";

const ROUND_COUNT = 25; // rounds 0..24
const LAST_ROUND = ROUND_COUNT - 1;
const BASE_RUN_MS = 6000; // full-season duration at 1x speed
const SPEED_OPTIONS = [0.25, 0.5, 1];
const DEFAULT_SPEED = 1;

// One hue per compared player, mirroring the bar chart's player ordering.
const PLAYER_COLORS = [
  '#34d399', // emerald-400
  '#38bdf8', // sky-400
  '#fbbf24', // amber-400
  '#a78bfa', // violet-400
  '#f472b6', // pink-400
  '#2dd4bf', // teal-400
  '#818cf8', // indigo-400
  '#fb923c', // orange-400
  '#a3e635', // lime-400
  '#22d3ee', // cyan-400
];

// Same C1..C5-then-external ordering the comparison table and bar chart use.
const DISPLAY_ORDER = ['count1', 'count2', 'count3', 'count4', 'count5', 'betfair', 'espn', 'byb'];

const DATASET_LABELS = {
  count1: 'BG 1',
  count2: 'BG 2',
  count3: 'BG 3',
  count4: 'BG 4',
  count5: 'BG 5',
  betfair: 'Betfair',
  espn: 'ESPN',
  byb: 'BYB',
};

// Clean y-axis ceiling + tick step, matching the other SVG charts.
const niceScale = (maxValue, maxTicks = 5) => {
  if (!maxValue || maxValue <= 0) return { max: 4, step: 1 };
  const candidates = [];
  for (let p = -1; p <= 3; p++) {
    const mag = Math.pow(10, p);
    [1, 2, 2.5, 5].forEach((m) => candidates.push(m * mag));
  }
  candidates.sort((a, b) => a - b);
  for (const step of candidates) {
    const max = Math.ceil(maxValue / step - 1e-9) * step;
    if (max / step <= maxTicks) return { max, step };
  }
  const step = candidates[candidates.length - 1];
  return { max: Math.ceil(maxValue / step) * step, step };
};

const toCumulative = (rounds) => {
  let running = 0;
  return rounds.map((v) => {
    running += Number(v) || 0;
    return running;
  });
};

// Cumulative value at a (possibly fractional) round, linearly interpolated so
// playback moves smoothly rather than snapping round to round.
const valueAt = (values, progress) => {
  const full = Math.floor(progress);
  const base = Number(values[full]) || 0;
  if (progress <= full || full >= LAST_ROUND) return base;
  const next = Number(values[full + 1]) || 0;
  return base + (next - base) * (progress - full);
};

const selectArrowStyle = {
  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%2394a3b8'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
  backgroundRepeat: 'no-repeat',
  backgroundPosition: 'right 6px center',
  backgroundSize: '12px',
};

const controlIconButtonClass =
  'w-6 h-6 flex items-center justify-center rounded text-slate-300 hover:text-white hover:bg-slate-700/60 transition-colors disabled:opacity-40 disabled:cursor-not-allowed';

const getPlayerImage = (playerName) => {
  const player = aflPlayerData.find((p) => p.player_name === playerName);
  return player ? player.image_src : null;
};

const initialsFor = (name) =>
  (name || '')
    .split(' ')
    .filter(Boolean)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

/**
 * Cumulative season lines for the compared players. Drawing is clipped to the
 * current playback position so a run reveals the season round by round.
 */
const ProgressionPlot = ({ series, progress, runActive, paused, hoverSeries, onHoverSeries }) => {
  const wrapRef = useRef(null);
  const [measuredW, setMeasuredW] = useState(420);
  const [hoverRound, setHoverRound] = useState(null);
  // Once a run has started the draw-in must not replay when playback finishes
  // and the chart settles back to the full season.
  const [played, setPlayed] = useState(false);
  const clipId = `cvpc-avatar-${useId().replace(/[^a-zA-Z0-9]/g, '')}`;

  useLayoutEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const update = () => setMeasuredW(Math.max(240, el.clientWidth));
    update();
    let ro;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(update);
      ro.observe(el);
    } else {
      window.addEventListener('resize', update);
    }
    return () => {
      if (ro) ro.disconnect();
      else window.removeEventListener('resize', update);
    };
  }, []);

  const geom = useMemo(() => {
    const padL = 26;
    const padR = 30; // room for the avatar markers parked at the line ends
    const padT = 16;
    const padB = 20;
    const svgW = measuredW;
    const svgH = 240;
    const innerW = svgW - padL - padR;
    const innerH = svgH - padT - padB;

    const peak = Math.max(0, ...series.map((s) => s.cumulative[LAST_ROUND] || 0));
    // As many gridlines as fit at ~20px apart, so high-vote comparisons still
    // get fine increments instead of a coarse scale.
    const tickBudget = Math.max(4, Math.floor(innerH / 20));
    const { max: yMax, step } = niceScale(peak, tickBudget);

    const yFor = (v) => padT + innerH - (Math.max(0, v) / yMax) * innerH;
    const xFor = (i) => padL + (innerW * i) / LAST_ROUND;
    const slot = innerW / LAST_ROUND;

    const ticks = [];
    for (let t = 0; t <= Math.round(yMax / step); t++) {
      const v = t * step;
      ticks.push({ v, y: yFor(v) });
    }

    // Avatars park at each line's final point; nudge them apart vertically so
    // players on similar totals don't stack their photos on top of each other.
    const spacing = Math.min(19, series.length > 1 ? innerH / (series.length - 1) : innerH);
    const targets = series
      .map((s, i) => ({ i, y: yFor(s.cumulative[LAST_ROUND] || 0) }))
      .sort((a, b) => a.y - b.y);
    let prev = -Infinity;
    for (const t of targets) {
      t.adj = Math.max(t.y, prev + spacing);
      prev = t.adj;
    }
    // If that pass pushed anyone past the baseline, walk the stack back up.
    let nextTop = padT + innerH;
    for (let k = targets.length - 1; k >= 0; k--) {
      targets[k].adj = Math.min(targets[k].adj, nextTop);
      nextTop = targets[k].adj - spacing;
    }
    const endY = {};
    targets.forEach((t) => {
      endY[t.i] = Math.max(padT - 4, t.adj);
    });

    return { padL, padR, padT, padB, svgW, svgH, slot, yFor, xFor, ticks, endY };
  }, [series, measuredW]);

  const { padL, padR, padT, padB, svgW, svgH, slot, yFor, xFor, ticks, endY } = geom;
  const baselineY = yFor(0);
  const lastX = xFor(LAST_ROUND);
  // Photos ride the line tip while a season is playing, then settle on the rail.
  const settled = !runActive && progress >= LAST_ROUND;

  // Path up to the current playback position (full path when idle at the end).
  const pathFor = (cumulative) => {
    const upto = Math.min(LAST_ROUND, progress);
    const full = Math.floor(upto);
    const pts = [];
    for (let r = 0; r <= full; r++) {
      pts.push(`${xFor(r).toFixed(1)},${yFor(cumulative[r]).toFixed(1)}`);
    }
    if (upto > full) {
      pts.push(`${xFor(upto).toFixed(1)},${yFor(valueAt(cumulative, upto)).toFixed(1)}`);
    }
    return `M${pts.join(' L')}`;
  };

  const leaderIndex = useMemo(() => {
    let best = 0;
    series.forEach((s, i) => {
      if (valueAt(s.cumulative, progress) > valueAt(series[best].cumulative, progress)) best = i;
    });
    return best;
  }, [series, progress]);

  const activeIndex = hoverSeries != null ? hoverSeries : leaderIndex;
  const active = series[activeIndex];
  const displayRound = Math.min(LAST_ROUND, Math.floor(progress));

  // Remounting replays the draw-in when players or the dataset change.
  const animKey = series.map((s) => `${s.name}:${(s.cumulative[LAST_ROUND] || 0).toFixed(2)}`).join('|');

  useEffect(() => {
    setPlayed(false);
  }, [animKey]);

  useEffect(() => {
    if (runActive) setPlayed(true);
  }, [runActive]);

  return (
    <div ref={wrapRef} className="relative w-full">
      <style>{`
        @keyframes cvpcDraw { from { stroke-dashoffset: 1; } to { stroke-dashoffset: 0; } }
        .cvpc-draw { animation: cvpcDraw 0.9s cubic-bezier(0.22, 1, 0.36, 1) backwards; }
        @keyframes cvpcFade { from { opacity: 0; } to { opacity: 1; } }
        .cvpc-fade { animation: cvpcFade 0.5s ease 0.35s backwards; }
      `}</style>
      <svg
        width={svgW}
        height={svgH}
        className="block"
        role="img"
        aria-label="Cumulative votes by round for compared players"
        onMouseLeave={() => setHoverRound(null)}
      >
        <defs>
          {/* Circular mask for the avatar photos (local coords) */}
          <clipPath id={clipId}>
            <circle cx={0} cy={0} r={8} />
          </clipPath>
        </defs>

        {/* Gridlines + y labels */}
        {ticks.map((t, i) => (
          <g key={i}>
            <line x1={padL} y1={t.y} x2={svgW - padR} y2={t.y} stroke="#1e293b" strokeWidth="1" />
            <text x={padL - 6} y={t.y + 3} textAnchor="end" className="fill-slate-500" style={{ fontSize: 9, fontVariantNumeric: 'tabular-nums' }}>
              {Number.isInteger(t.v) ? t.v : t.v.toFixed(1)}
            </text>
          </g>
        ))}

        <g key={animKey}>
          {series.map((s, i) => {
            const dim = hoverSeries != null && hoverSeries !== i;
            return (
              <path
                key={s.name}
                d={pathFor(s.cumulative)}
                fill="none"
                stroke={s.color}
                strokeWidth={hoverSeries === i ? 2.8 : 2}
                strokeLinejoin="round"
                strokeLinecap="round"
                pathLength="1"
                strokeDasharray="1"
                className={played || runActive ? undefined : 'cvpc-draw'}
                style={{
                  animationDelay: `${Math.min(i * 60, 400)}ms`,
                  opacity: dim ? 0.15 : 1,
                  transition: 'opacity 0.15s, stroke-width 0.15s',
                }}
              />
            );
          })}
        </g>

        {/* Hover guide tracking the active player */}
        {hoverRound != null && !runActive && (
          <g pointerEvents="none">
            <line x1={xFor(hoverRound)} y1={padT} x2={xFor(hoverRound)} y2={baselineY} stroke="#475569" strokeWidth="1" strokeDasharray="2 3" />
            <circle cx={xFor(hoverRound)} cy={yFor(active.cumulative[hoverRound])} r={3.4} fill={active.color} stroke="#0a1120" strokeWidth="1" />
            <text
              x={Math.min(Math.max(xFor(hoverRound), padL + 26), svgW - padR - 26)}
              y={Math.max(padT + 8, yFor(active.cumulative[hoverRound]) - 8)}
              textAnchor="middle"
              style={{ fontSize: 9, fontWeight: 700, fontVariantNumeric: 'tabular-nums', fill: active.color }}
            >
              {active.cumulative[hoverRound].toFixed(2)}
            </text>
          </g>
        )}

        {/* Live round indicator during playback */}
        {runActive && (
          <text
            x={padL + 6}
            y={padT + 14}
            className="fill-slate-300"
            style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.14em', fontVariantNumeric: 'tabular-nums' }}
          >
            ROUND {displayRound}
          </text>
        )}

        {/* Round labels */}
        {Array.from({ length: ROUND_COUNT }, (_, i) => i).map((i) => (
          <text
            key={i}
            x={xFor(i)}
            y={svgH - 6}
            textAnchor="middle"
            className={hoverRound === i ? 'fill-slate-300' : 'fill-slate-500'}
            style={{ fontSize: 7.5, fontVariantNumeric: 'tabular-nums' }}
          >
            {i}
          </text>
        ))}

        {/* Hover hit areas, drawn last so they sit above the plot */}
        {Array.from({ length: ROUND_COUNT }, (_, i) => i).map((i) => (
          <rect
            key={i}
            x={xFor(i) - slot / 2}
            y={padT}
            width={slot}
            height={svgH - padT - padB}
            fill="transparent"
            onMouseEnter={() => setHoverRound(i)}
          >
            <title>{`Round ${i} — ${active.name}: ${active.cumulative[i].toFixed(2)} total`}</title>
          </rect>
        ))}

        {/* Player photos: ride each line's tip during a run, then settle on the
            nudged rail at the end of the season. Drawn last so they stay
            hoverable above the round hit areas. */}
        {series.map((s, i) => {
          const dim = hoverSeries != null && hoverSeries !== i;
          const cx = settled ? lastX + 13 : xFor(Math.min(LAST_ROUND, progress));
          const cy = settled ? endY[i] : yFor(valueAt(s.cumulative, progress));
          const endPointY = yFor(s.cumulative[LAST_ROUND] || 0);
          return (
            <g
              key={s.name}
              className={played || runActive ? undefined : 'cvpc-fade'}
              style={{ opacity: dim ? 0.15 : 1, transition: 'opacity 0.15s', cursor: 'default' }}
              onMouseEnter={() => onHoverSeries(i)}
              onMouseLeave={() => onHoverSeries(null)}
            >
              <title>{`${s.name} — ${(s.cumulative[LAST_ROUND] || 0).toFixed(2)} projected votes`}</title>
              {/* Connector from the line end to the nudged photo */}
              {settled && (
                <line x1={lastX} y1={endPointY} x2={cx - 9} y2={cy} stroke={s.color} strokeWidth="1" opacity="0.5" />
              )}
              <g
                style={{
                  transform: `translate(${cx}px, ${cy}px)`,
                  transition: runActive && !paused ? 'none' : 'transform 0.5s cubic-bezier(0.22, 1, 0.36, 1)',
                }}
              >
                <circle cx={0} cy={0} r={9} fill="#0a1120" stroke={s.color} strokeWidth={hoverSeries === i ? 2 : 1.5} />
                {s.image ? (
                  <image
                    href={s.image}
                    x={-8}
                    y={-8}
                    width={16}
                    height={16}
                    clipPath={`url(#${clipId})`}
                    preserveAspectRatio="xMidYMid slice"
                  />
                ) : (
                  <text x={0} y={2.5} textAnchor="middle" className="fill-slate-300" style={{ fontSize: 7, fontWeight: 700 }}>
                    {initialsFor(s.name)}
                  </text>
                )}
              </g>
            </g>
          );
        })}

        {/* Baseline */}
        <line x1={padL} y1={baselineY} x2={svgW - padR} y2={baselineY} stroke="#334155" strokeWidth="1.25" />
      </svg>

      {/* Legend: totals track playback; hovering a chip isolates that line */}
      <div className="mt-3 pt-3 border-t border-slate-800/70 flex flex-wrap gap-1.5" onMouseLeave={() => onHoverSeries(null)}>
        {series.map((s, i) => {
          const dim = hoverSeries != null && hoverSeries !== i;
          const value = valueAt(s.cumulative, progress);
          return (
            <div
              key={s.name}
              onMouseEnter={() => onHoverSeries(i)}
              className="flex items-center gap-1.5 px-2 py-1 rounded-md border bg-slate-900/50 border-slate-800"
              style={{ opacity: dim ? 0.4 : 1, cursor: 'default' }}
            >
              {s.image ? (
                <img src={s.image} alt={s.name} className="w-4 h-4 rounded-full object-cover ring-1 ring-slate-700/60" />
              ) : (
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
              )}
              <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: s.color }} />
              <span className="text-[10px] font-semibold text-slate-300 truncate max-w-[92px]">{s.name}</span>
              <span className="text-[11px] font-mono font-bold tabular-nums" style={{ color: s.color }}>
                {value.toFixed(2)}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ComparisonVoteProgressionChart = ({ selectedPlayers = [], voteData }) => {
  const { getEnabledKeys } = useSettings();
  const enabledKeys = getEnabledKeys();
  const enabledKey = enabledKeys.join(',');

  // Local to this chart so it never affects the dataset selection other pages use.
  const [dataset, setDataset] = useState('all');
  const [hoverSeries, setHoverSeries] = useState(null);

  // Fall back to All when the chosen dataset gets disabled in settings.
  useEffect(() => {
    if (dataset !== 'all' && !enabledKey.split(',').includes(dataset)) {
      setDataset('all');
    }
  }, [enabledKey, dataset]);

  // ---- Playback engine (frame-based so pause/speed changes apply live) ----
  const [progress, setProgress] = useState(LAST_ROUND);
  const [runActive, setRunActive] = useState(false);
  const [paused, setPaused] = useState(false);
  const [speed, setSpeed] = useState(DEFAULT_SPEED);
  const rafRef = useRef(null);
  const lastTsRef = useRef(0);
  const progressRef = useRef(LAST_ROUND);
  const pausedRef = useRef(false);
  const speedRef = useRef(DEFAULT_SPEED);

  useEffect(() => {
    pausedRef.current = paused;
  }, [paused]);
  useEffect(() => {
    speedRef.current = speed;
  }, [speed]);

  const stopLoop = useCallback(() => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const tick = useCallback((now) => {
    const dt = Math.min(100, now - lastTsRef.current);
    lastTsRef.current = now;
    if (!pausedRef.current) {
      progressRef.current = Math.min(
        LAST_ROUND,
        progressRef.current + (dt / BASE_RUN_MS) * LAST_ROUND * speedRef.current
      );
      setProgress(progressRef.current);
    }
    if (progressRef.current >= LAST_ROUND) {
      setRunActive(false);
      setPaused(false);
      return;
    }
    rafRef.current = requestAnimationFrame(tick);
  }, []);

  const startLoop = useCallback(() => {
    stopLoop();
    setRunActive(true);
    setPaused(false);
    lastTsRef.current = performance.now();
    rafRef.current = requestAnimationFrame(tick);
  }, [stopLoop, tick]);

  const startRun = useCallback(() => {
    progressRef.current = 0;
    setProgress(0);
    startLoop();
  }, [startLoop]);

  const playToggle = useCallback(() => {
    if (runActive) {
      setPaused((p) => !p);
      return;
    }
    if (progressRef.current >= LAST_ROUND) {
      progressRef.current = 0;
      setProgress(0);
    }
    startLoop();
  }, [runActive, startLoop]);

  const stopRun = useCallback(() => {
    stopLoop();
    progressRef.current = LAST_ROUND;
    setProgress(LAST_ROUND);
    setRunActive(false);
    setPaused(false);
  }, [stopLoop]);

  const seekTo = useCallback((round) => {
    progressRef.current = Math.max(0, Math.min(LAST_ROUND, round));
    setProgress(progressRef.current);
  }, []);

  useEffect(() => stopLoop, [stopLoop]);

  // ---- Series: one cumulative line per compared player ----
  const series = useMemo(() => {
    if (!voteData?.leaderboard || selectedPlayers.length === 0) return [];
    const sourceKeys = dataset === 'all' ? enabledKeys : [dataset];
    if (!sourceKeys.length) return [];

    const rows = selectedPlayers
      .map((playerName) => {
        const playerRow = voteData.leaderboard.find((p) => p.name === playerName);
        if (!playerRow) return null;

        const sources = getPlayerRoundsByDataset(playerRow, voteData, sourceKeys);
        if (!sources.length) return null;

        // Average the selected sources per round, matching how the table and
        // bar chart average the same datasets.
        const perRound = Array.from({ length: ROUND_COUNT }, (_, r) => {
          const sum = sources.reduce((acc, s) => acc + (Number(s.rounds[r]) || 0), 0);
          return sum / sources.length;
        });
        const cumulative = toCumulative(perRound);

        return {
          name: playerRow.name,
          team: playerRow.team,
          image: getPlayerImage(playerRow.name),
          cumulative,
          total: cumulative[LAST_ROUND] || 0,
        };
      })
      .filter(Boolean);

    // Highest season total first, so colours track the same order as the table.
    rows.sort((a, b) => b.total - a.total);
    return rows.map((r, i) => ({ ...r, color: PLAYER_COLORS[i % PLAYER_COLORS.length] }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPlayers, voteData, dataset, enabledKey]);

  // Changing the player set or dataset cancels any run in progress.
  const seriesKey = series.map((s) => s.name).join('|');
  useEffect(() => {
    stopLoop();
    progressRef.current = LAST_ROUND;
    setProgress(LAST_ROUND);
    setRunActive(false);
    setPaused(false);
    setHoverSeries(null);
  }, [seriesKey, dataset, stopLoop]);

  const displayRound = Math.min(LAST_ROUND, Math.floor(progress));
  const hasSeries = series.length > 0;

  return (
    <div className="bg-slate-950/60 rounded-xl border border-slate-800 p-3 w-full h-full flex flex-col min-h-0">
      {/* Controls */}
      <div className="flex flex-wrap items-center gap-2 mb-3 flex-shrink-0">
        <div className="flex items-center gap-1.5 bg-slate-900/50 border border-slate-700/60 rounded-lg px-2 h-8">
          <button
            onClick={playToggle}
            disabled={!hasSeries}
            className={controlIconButtonClass}
            title={runActive && !paused ? 'Pause' : 'Play'}
            aria-label={runActive && !paused ? 'Pause progression' : 'Play progression'}
          >
            {runActive && !paused ? (
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" />
              </svg>
            ) : (
              <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                <path d="M8 5.14v13.72c0 .86.94 1.39 1.68.94l11.02-6.86a1.1 1.1 0 000-1.88L9.68 4.2A1.1 1.1 0 008 5.14z" />
              </svg>
            )}
          </button>
          <button
            onClick={stopRun}
            disabled={!hasSeries || (!runActive && progress >= LAST_ROUND)}
            className={controlIconButtonClass}
            title="Stop and show final totals"
            aria-label="Stop progression"
          >
            <svg className="w-3 h-3" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
              <rect x="6" y="6" width="12" height="12" rx="1.5" />
            </svg>
          </button>
          <div className="w-px h-4 bg-slate-700/60" />
          <span className="text-[10px] font-mono font-semibold tabular-nums text-emerald-300 w-9 text-center">
            RD {displayRound}
          </span>
          <input
            type="range"
            min={0}
            max={LAST_ROUND}
            step={1}
            value={displayRound}
            onChange={(e) => seekTo(Number(e.target.value))}
            disabled={!hasSeries}
            className="cvpc-scrub w-16 xl:w-20 h-1 bg-slate-800 rounded-full appearance-none cursor-pointer focus:outline-none disabled:opacity-40"
            title="Jump to a specific round"
            aria-label="Jump to round"
          />
          <div className="w-px h-4 bg-slate-700/60" />
          <select
            value={speed}
            onChange={(e) => setSpeed(Number(e.target.value))}
            className="bg-transparent text-[10px] font-mono font-semibold text-slate-300 focus:outline-none cursor-pointer"
            title="Playback speed"
            aria-label="Playback speed"
          >
            {SPEED_OPTIONS.map((s) => (
              <option key={s} value={s} className="bg-slate-900">
                {s.toFixed(2)}&times;
              </option>
            ))}
          </select>
        </div>

        <select
          value={dataset}
          onChange={(e) => setDataset(e.target.value)}
          className="bg-slate-900/50 border border-slate-700/60 text-[10px] font-mono text-white rounded-lg pl-2 pr-6 h-8 focus:outline-none focus:border-emerald-500/60 cursor-pointer appearance-none transition-colors"
          style={selectArrowStyle}
          title="Choose dataset for the progression"
          aria-label="Dataset"
        >
          <option value="all">Dataset: All</option>
          {DISPLAY_ORDER.filter((k) => enabledKeys.includes(k)).map((k) => (
            <option key={k} value={k}>
              Dataset: {DATASET_LABELS[k] || k}
            </option>
          ))}
        </select>

        <button
          onClick={startRun}
          disabled={!hasSeries}
          className="h-8 px-2.5 text-[10px] font-mono font-semibold rounded-lg bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/25 hover:border-emerald-500/50 transition-all flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed"
          title="Animate the vote progression round by round"
        >
          <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M8 5.14v13.72c0 .86.94 1.39 1.68.94l11.02-6.86a1.1 1.1 0 000-1.88L9.68 4.2A1.1 1.1 0 008 5.14z" />
          </svg>
          {runActive ? 'Restart' : 'Run'}
        </button>
      </div>

      <style>{`
        .cvpc-scrub::-webkit-slider-thumb {
          appearance: none;
          height: 11px;
          width: 11px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #0f172a;
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.4);
        }
        .cvpc-scrub::-moz-range-thumb {
          height: 11px;
          width: 11px;
          border-radius: 50%;
          background: #10b981;
          cursor: pointer;
          border: 2px solid #0f172a;
          box-shadow: 0 0 0 1px rgba(16, 185, 129, 0.4);
        }
      `}</style>

      {/* Plot */}
      {hasSeries ? (
        <ProgressionPlot
          series={series}
          progress={progress}
          runActive={runActive}
          paused={paused}
          hoverSeries={hoverSeries}
          onHoverSeries={setHoverSeries}
        />
      ) : (
        <div className="flex-1 min-h-[16rem] flex items-center justify-center rounded-lg border border-dashed border-slate-800/80 bg-[#0a1120]/40">
          <p className="text-slate-500 text-xs font-mono">Select players to see comparison data</p>
        </div>
      )}
    </div>
  );
};

export default ComparisonVoteProgressionChart;
